import React from 'react';
import ReactDOM from 'react-dom'; // Add this import
import { Link, NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  HelpCircle,
  Bell,
  LogOut,
  LogIn,
  Menu,
  X,
  LayoutDashboard,
  Wallet,
  IdCard,
  ChevronDown,
  CreditCard,
} from 'lucide-react';
import i18n from '../../i18n';
import { getLoggedUser } from '../../state/user';
import { Clock } from '../helpers/clock';
import '../../styles/components/navbar.scss';

const user = getLoggedUser();
const roles = {
  A: i18n.t('admin'),
  C: i18n.t('client'),
  CE: i18n.t('ceo'),
  GM: i18n.t('gm'),
  F: i18n.t('finance'),
  HSE: i18n.t('hse'),
  O: i18n.t('operator'),
  M: i18n.t('manager'),
  PM: 'PM',
  OM: i18n.t('OM'),
  U: i18n.t('bidder'),
};

const changeLanguage = (lng) => {
  i18n.changeLanguage(lng);
  window.location.reload();
};

const logout = () => {
  localStorage.removeItem('is_logged_in');
  localStorage.removeItem('token');
  localStorage.removeItem('mzad_user_info');
  window.location.href = '/';
};

const modalRoot = document.getElementById('modal-root');

export class Navbar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mobileMenuOpen: false,
      scrolled: false,
      userDropdownOpen: false,
    };
    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    document.addEventListener('mousedown', this.handleClickOutside);

    // Prevent body scroll when mobile menu is open
    if (this.state.mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('mousedown', this.handleClickOutside);

    // Clean up body overflow
    document.body.style.overflow = '';
  }

  componentDidUpdate(prevProps, prevState) {
    // Prevent body scroll when mobile menu is open
    if (prevState.mobileMenuOpen !== this.state.mobileMenuOpen) {
      if (this.state.mobileMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }

  handleScroll = () => {
    const scrolled = window.scrollY > 20;
    if (scrolled !== this.state.scrolled) {
      this.setState({ scrolled });
    }
  };

  handleClickOutside = (event) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({ userDropdownOpen: false });
    }
  };

  toggleMobileMenu = () => {
    this.setState((prevState) => ({
      mobileMenuOpen: !prevState.mobileMenuOpen,
    }));
  };

  closeMobileMenu = () => {
    this.setState({ mobileMenuOpen: false });
  };

  toggleUserDropdown = () => {
    this.setState((prevState) => ({
      userDropdownOpen: !prevState.userDropdownOpen,
    }));
  };

  getInitials = (name) => {
    if (!name) return 'G';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  getDashboardRoute = () => {
    switch (user?.role) {
      case 'CE':
        return '/management';
      case 'C':
        return '/client-dashboard';
      case 'O':
        return '/delivery-site-list';
      default:
        return '/dashboard';
    }
  };

  // Render mobile menu as portal
  renderMobileMenu() {
    const current_lng = localStorage.getItem('I18N_LANGUAGE') || 'en';
    const { wallet_amount, is_loading, is_logged_in, due_amount } = this.props;
    const { mobileMenuOpen } = this.state;

    if (!mobileMenuOpen) return null;

    const mobileMenuContent = (
      <>
        {/* Overlay */}
        <div className="prof-navbar__overlay" onClick={this.closeMobileMenu}></div>

        {/* Mobile Menu */}
        <div className={`prof-navbar__mobile prof-navbar__mobile--open`}>
          <div className="prof-navbar__mobile-header">
            <span className="prof-navbar__mobile-title">{i18n.t('menu')}</span>
            <button
              className="prof-navbar__mobile-close"
              onClick={this.closeMobileMenu}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* Mobile User Info */}
          {is_logged_in && (
            <div className="prof-navbar__mobile-user">
              <div className="prof-navbar__avatar prof-navbar__avatar--large">
                {this.getInitials(user?.name)}
              </div>
              <div className="prof-navbar__mobile-user-info">
                <span className="prof-navbar__mobile-user-name">
                  {user?.name || i18n.t('guest')}
                </span>
                <span className="prof-navbar__mobile-user-role">
                  {user?.role ? roles[user.role] : 'Guest'}
                </span>
              </div>
            </div>
          )}

          {/* Mobile Navigation */}
          <ul className="prof-navbar__mobile-nav">
            <li>
              <NavLink
                to="/"
                className="prof-navbar__mobile-nav-link"
                onClick={this.closeMobileMenu}
                exact
              >
                <Home size={20} />
                <span>{i18n.t('home')}</span>
              </NavLink>
            </li>
            {is_logged_in && (
              <li>
                <NavLink
                  to={this.getDashboardRoute()}
                  className="prof-navbar__mobile-nav-link"
                  onClick={this.closeMobileMenu}
                >
                  <LayoutDashboard size={20} />
                  <span>{i18n.t('dashboard')}</span>
                </NavLink>
              </li>
            )}
            <li>
              <NavLink
                to="/contact"
                className="prof-navbar__mobile-nav-link"
                onClick={this.closeMobileMenu}
              >
                <HelpCircle size={20} />
                <span>{i18n.t('contactus')}</span>
              </NavLink>
            </li>
          </ul>

          {/* Mobile Actions */}
          {is_logged_in ? (
            <div className="prof-navbar__mobile-actions">
              <Link
                to="/wallet"
                className="prof-navbar__mobile-btn prof-navbar__mobile-btn--wallet"
                onClick={this.closeMobileMenu}
              >
                <Wallet size={20} />
                <div className="prof-navbar__mobile-btn-info">
                  <span className="prof-navbar__mobile-btn-label">{i18n.t('wallet')}</span>
                  <span className="prof-navbar__mobile-btn-value">
                    {is_loading ? '...' : `${i18n.t('omr')} ${wallet_amount}`}
                  </span>
                </div>
              </Link>

              {/* Due Amount Link */}
              {user?.role === 'U' && due_amount > 0 && (
                <Link
                  to="/mzadcom-payment"
                  className="prof-navbar__mobile-btn prof-navbar__mobile-btn--wallet"
                  onClick={this.closeMobileMenu}
                >
                  <CreditCard size={20} />
                  <div className="prof-navbar__mobile-btn-info">
                    <span className="prof-navbar__mobile-btn-label">{i18n.t('mzadcom_due')}</span>
                    <span className="prof-navbar__mobile-btn-value">
                      {is_loading ? '...' : `${i18n.t('omr')} ${due_amount}`}
                    </span>
                  </div>
                </Link>
              )}
              <button
                onClick={() => {
                  logout();
                  this.closeMobileMenu();
                }}
                className="prof-navbar__mobile-btn prof-navbar__mobile-btn--logout"
              >
                <LogOut size={20} />
                <span>{i18n.t('logout')}</span>
              </button>
            </div>
          ) : (
            <div className="prof-navbar__mobile-actions">
              <NavLink
                to="/login"
                className="button1 button1-outline"
                onClick={this.closeMobileMenu}
                exact
              >
                <LogIn size={20} />
                <span>{i18n.t('login')}</span>
              </NavLink>
              <NavLink
                to="/comp-registration"
                className="button1 button1-gradient"
                onClick={this.closeMobileMenu}
                exact
              >
                <span>{i18n.t('registration')}</span>
              </NavLink>
            </div>
          )}

          {/* Mobile Footer */}
          <div className="prof-navbar__mobile-footer">
            <button
              onClick={() => changeLanguage(current_lng === 'ar' ? 'en' : 'ar')}
              className="prof-navbar__mobile-lang"
            >
              <img
                src={
                  process.env.PUBLIC_URL +
                  `/assets/images/flags/${current_lng === 'ar' ? 'en' : 'ar'}.svg`
                }
                alt={current_lng === 'ar' ? 'EN' : 'عربي'}
                className="prof-navbar__flag"
              />
              <span>{current_lng === 'ar' ? 'English' : 'عربي'}</span>
            </button>
          </div>
        </div>
      </>
    );

    // Render to portal
    return ReactDOM.createPortal(mobileMenuContent, modalRoot);
  }

  render() {
    const current_lng = localStorage.getItem('I18N_LANGUAGE') || 'en';
    const { is_logged_in, due_amount, is_loading } = this.props;
    const { mobileMenuOpen, scrolled, userDropdownOpen } = this.state;

    return (
      <>
        <nav className={`prof-navbar ${scrolled ? 'prof-navbar--scrolled' : ''}`}>
          <div className="prof-navbar__container">
            {/* Logo */}
            <div className="prof-navbar__logo">
              <Link to="/" className="prof-navbar__logo-link">
                {/* <img
                  src={process.env.PUBLIC_URL + '/assets/images/logo-org.png'}
                  alt="Mzadcom Logo"
                  className="prof-navbar__logo-img"
                /> */}
                <img
                src={process.env.PUBLIC_URL + "/assets/logo/logo-l.png"}
                alt="prof-navbar__logo-img"
                width="160"
              />
              </Link>
            </div>

            {/* Main Navigation */}
            <ul className="prof-navbar__nav">
              <li className="prof-navbar__nav-item">
                <NavLink to="/" className="prof-navbar__nav-link" exact>
                  <span>{i18n.t('home')}</span>
                </NavLink>
              </li>
              <li className="prof-navbar__nav-item">
                <NavLink to="/auctions" className="prof-navbar__nav-link" exact>
                  <span>{i18n.t('all_auctions')}</span>
                </NavLink>
              </li>
              
              <li className="prof-navbar__nav-item">
                <NavLink to="/contact" className="prof-navbar__nav-link">
                  <span>{i18n.t('contactus')}</span>
                </NavLink>
              </li>
            </ul>

            {/* Right Actions */}
            <div className="prof-navbar__actions">
              {/* Notification */}
              {is_logged_in && (
                <Link to="/notifications" className="prof-navbar__icon-btn">
                  <Bell size={20} />
                </Link>
              )}

              {/* Clock */}
              {/* <div className="prof-navbar__clock">
                <Clock lang={current_lng} />
              </div> */}

              {/* Language Switcher */}
              <button
                onClick={() => changeLanguage(current_lng === 'ar' ? 'en' : 'ar')}
                className="prof-navbar__lang-btn"
              >
                <img
                  src={
                    process.env.PUBLIC_URL +
                    `/assets/images/flags/${current_lng === 'ar' ? 'en' : 'ar'}.svg`
                  }
                  alt={current_lng === 'ar' ? 'EN' : 'عربي'}
                  className="prof-navbar__flag"
                />
              </button>

              {/* User Section or Auth Buttons */}
              {is_logged_in ? (
                <div className="prof-navbar__user-section" ref={this.dropdownRef}>
                  <button
                    className="prof-navbar__user-trigger"
                    onClick={this.toggleUserDropdown}
                    aria-expanded={userDropdownOpen}
                  >
                    <div className="prof-navbar__avatar">{this.getInitials(user?.name)}</div>
                    <span className="prof-navbar__username">{user?.name || i18n.t('guest')}</span>
                    <ChevronDown
                      size={16}
                      className={`prof-navbar__chevron ${
                        userDropdownOpen ? 'prof-navbar__chevron--open' : ''
                      }`}
                    />
                  </button>

                  {/* User Dropdown */}
                  {userDropdownOpen && (
                    <div className="prof-navbar__dropdown">
                      <div className="prof-navbar__dropdown-item prof-navbar__dropdown-item--info">
                        <IdCard size={18} />
                        <div className="prof-navbar__dropdown-info">
                          <span className="prof-navbar__dropdown-label">{i18n.t('role')}</span>
                          <span className="prof-navbar__dropdown-value">
                            {user?.role ? roles[user.role] : 'Guest'}
                          </span>
                        </div>
                      </div>
                      <Link
                        to={this.getDashboardRoute()}
                        className="prof-navbar__dropdown-item"
                        onClick={this.toggleUserDropdown}
                      >
                        <LayoutDashboard size={18} />
                        <span>{i18n.t('dashboard')}</span>
                      </Link>
                      <Link
                        to="/wallet"
                        className="prof-navbar__dropdown-item"
                        onClick={this.toggleUserDropdown}
                      >
                        <Wallet size={18} />
                        <div className="prof-navbar__dropdown-info">
                          <span className="">{i18n.t('wallet')}</span>
                        </div>
                      </Link>
                      {/* Due Amount Link - Only show if there's a due amount */}
                      {user?.role === 'U' && due_amount > 0 && (
                        <Link
                          to="/mzadcom-payment"
                          className="prof-navbar__dropdown-item"
                          onClick={this.toggleUserDropdown}
                        >
                          <CreditCard size={18} />
                          <div className="prof-navbar__dropdown-info">
                            <span className="prof-navbar__dropdown-label">
                              {i18n.t('mzadcom_due')}
                            </span>
                            <span className="prof-navbar__dropdown-value--amount">
                              {is_loading ? '...' : `${i18n.t('omr')} ${due_amount}`}
                            </span>
                          </div>
                        </Link>
                      )}
                      <div className="prof-navbar__dropdown-divider"></div>
                      <button
                        className="prof-navbar__dropdown-item prof-navbar__dropdown-item--logout"
                        onClick={() => {
                          logout();
                          this.toggleUserDropdown();
                        }}
                      >
                        <LogOut size={18} />
                        <span>{i18n.t('logout')}</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="prof-navbar__auth">
                  <NavLink to="/login" className="button1 button1-outline" exact>
                    <LogIn size={18} />
                    <span>{i18n.t('login')}</span>
                  </NavLink>
                  <NavLink to="/comp-registration" className="button1 button1-gradient" exact>
                    <span>{i18n.t('registration')}</span>
                  </NavLink>
                </div>
              )}

              {/* Mobile Toggle */}
              <button
                className="prof-navbar__mobile-toggle"
                onClick={this.toggleMobileMenu}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

            </div>

              <div className="prof-navbar__logo">
              <Link to="/" className="prof-navbar__logo-link">
                {/* <img
                  src={process.env.PUBLIC_URL + '/assets/images/logo-org.png'}
                  alt="Mzadcom Logo"
                  className="prof-navbar__logo-img"
                /> */}
                <img
                src={process.env.PUBLIC_URL + "/assets/logo/logo-r.png"}
                alt="prof-navbar__logo-img"
                width="160"
              />
              </Link>
            </div>
            
          </div>
        </nav>

        {/* Render mobile menu as portal */}
        {this.renderMobileMenu()}
      </>
    );
  }
}
